import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { authAPI, setAuthToken, clearAuthToken } from '@services/api';
import { STORAGE_KEYS } from '@utils/constants';
import { getStorageItem, setStorageItem } from '@utils/helpers';
import type { User, LoginForm, RegisterForm, AuthContextType } from '@/types';

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string; refreshToken: string } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'UPDATE_USER'; payload: Partial<User> }
  | { type: 'CLEAR_ERROR' };

const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  // Start in loading state so ProtectedRoute waits for the hydration
  // effect to read localStorage before deciding to redirect.
  loading: true,
  error: null,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        loading: true,
        error: null,
      };

    case 'AUTH_SUCCESS':
      return {
        ...state,
        loading: false,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
        error: null,
      };

    case 'AUTH_FAILURE':
      return {
        ...state,
        loading: false,
        isAuthenticated: false,
        user: null,
        token: null,
        error: action.payload,
      };

    case 'AUTH_LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        loading: false,
        user: null,
        token: null,
        refreshToken: null,
        error: null,
      };

    case 'UPDATE_USER':
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : null,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

// Extended context type with OAuth support
interface ExtendedAuthContextType extends AuthContextType {
  loginWithTokens: (accessToken: string, refreshToken: string, userData?: Record<string, unknown>) => Promise<void>;
}

const AuthContext = createContext<ExtendedAuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = () => {
      const token = getStorageItem(STORAGE_KEYS.AUTH_TOKEN, null);
      const refreshToken = getStorageItem(STORAGE_KEYS.REFRESH_TOKEN, null);
      const userData = getStorageItem(STORAGE_KEYS.USER_DATA, null);

      if (token && refreshToken && userData) {
        setAuthToken(token);
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { user: userData, token, refreshToken },
        });
      } else {
        // No stored session — leave the user unauthenticated but clear
        // the initial loading flag so ProtectedRoute can redirect to login.
        dispatch({ type: 'AUTH_LOGOUT' });
      }
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginForm): Promise<void> => {
    dispatch({ type: 'AUTH_START' });

    try {
      const response = await authAPI.login(credentials);
      const { user, tokens } = response;
      const accessToken = tokens.access_token;
      const refreshToken = tokens.refresh_token;

      // Store tokens separately in localStorage
      setStorageItem(STORAGE_KEYS.AUTH_TOKEN, accessToken);
      setStorageItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      setStorageItem(STORAGE_KEYS.USER_DATA, user);
      setAuthToken(accessToken);

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, token: accessToken, refreshToken },
      });
    } catch (error: any) {
      dispatch({
        type: 'AUTH_FAILURE',
        payload: error.message || 'Login failed',
      });
      throw error;
    }
  };

  const register = async (userData: RegisterForm): Promise<void> => {
    dispatch({ type: 'AUTH_START' });

    try {
      const response = await authAPI.register(userData);
      const { user, tokens } = response;
      const accessToken = tokens.access_token;
      const refreshToken = tokens.refresh_token;

      // Store tokens separately in localStorage
      setStorageItem(STORAGE_KEYS.AUTH_TOKEN, accessToken);
      setStorageItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      setStorageItem(STORAGE_KEYS.USER_DATA, user);
      setAuthToken(accessToken);

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, token: accessToken, refreshToken },
      });
    } catch (error: any) {
      dispatch({
        type: 'AUTH_FAILURE',
        payload: error.message || 'Registration failed',
      });
      throw error;
    }
  };

  const logout = (): void => {
    clearAuthToken();
    dispatch({ type: 'AUTH_LOGOUT' });
  };

  const updateUser = (updates: Partial<User>): void => {
    if (state.user) {
      const updatedUser = { ...state.user, ...updates };
      setStorageItem(STORAGE_KEYS.USER_DATA, updatedUser);
      dispatch({
        type: 'UPDATE_USER',
        payload: updates,
      });
    }
  };

  const refreshAuthToken = async (): Promise<string> => {
    try {
      const response = await authAPI.refreshToken();
      const newAccessToken = response.tokens.access_token;
      const newRefreshToken = response.tokens.refresh_token;

      // Store both tokens - refresh token rotation for security
      setStorageItem(STORAGE_KEYS.AUTH_TOKEN, newAccessToken);
      setStorageItem(STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);
      setAuthToken(newAccessToken);

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user: state.user!, token: newAccessToken, refreshToken: newRefreshToken },
      });

      return newAccessToken;
    } catch (error) {
      logout();
      throw error;
    }
  };

  // Normalize user data from backend (handle snake_case -> camelCase and compute name)
  const normalizeUser = (userData: any): User => {
    return {
      ...userData,
      id: userData.id || userData.user_id,
      name: userData.name ||
        (userData.first_name || userData.last_name
          ? `${userData.first_name || ''} ${userData.last_name || ''}`.trim()
          : undefined),
      createdAt: userData.createdAt || userData.created_at,
      updatedAt: userData.updatedAt || userData.updated_at,
    };
  };

  // Login with tokens directly (for OAuth callback or magic link)
  // If userData is provided, use it directly instead of making another API call
  const loginWithTokens = async (accessToken: string, refreshToken: string, userData?: Record<string, unknown>): Promise<void> => {
    dispatch({ type: 'AUTH_START' });

    try {
      // Store tokens
      setStorageItem(STORAGE_KEYS.AUTH_TOKEN, accessToken);
      setStorageItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      setAuthToken(accessToken);

      let rawUser: Record<string, unknown> | null = null;

      if (userData) {
        // Use provided user data directly (avoids race condition with token not yet being set)
        rawUser = userData;
      } else {
        // Fetch user profile with the new token (fallback for OAuth flows)
        // The API returns { success: true, data: { user: {...} } } structure
        const response = await authAPI.getProfile();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rawUser = (response.data as any)?.user || response.data;
      }

      if (!rawUser) {
        throw new Error('Failed to fetch user profile');
      }

      const user = normalizeUser(rawUser);
      setStorageItem(STORAGE_KEYS.USER_DATA, user);

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, token: accessToken, refreshToken },
      });
    } catch (error: any) {
      clearAuthToken();
      dispatch({
        type: 'AUTH_FAILURE',
        payload: error.message || 'Authentication failed',
      });
      throw error;
    }
  };

  const value: ExtendedAuthContextType = {
    ...state,
    emailVerified: state.user?.email_verified ?? false,
    login,
    register,
    logout,
    updateUser,
    refreshToken: refreshAuthToken,
    loginWithTokens,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): ExtendedAuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
